import React, { useState, useRef } from "react";
import { useForm, useFieldArray, SubmitHandler, FieldErrors } from "react-hook-form";
import { Helmet } from "react-helmet";
import ReCAPTCHA from "react-google-recaptcha";
import { AlertCircle, FileText, Mail, Globe, User, Plus, X, Loader2, ArrowRight } from "lucide-react";
import { getReport } from "../../lib/api/requests";
import ReportTable, { ReportReact } from "../../components/Report/ReportTable";
import { LoadingLoader } from "../../components/Loader/LopadingLoader";
import PrivatePolicy from "../../lib/pdf/PrivacyPolicy.pdf";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import "../../styles/Report.scss";

type FormValues = {
  website: {
    url: string | null;
  }[];
  email: string;
  privacyPolicy: boolean;
  name?: string;
};

const validateWebsite = (url: string | null): boolean => {
  if (!url) return false;
  let testUrl = url.trim();
  if (testUrl.length === 0) return false;
  if (!testUrl.includes('.')) return false;
  if (testUrl === '.' || testUrl.startsWith('.') || testUrl.endsWith('.')) return false;
  if (!/^(https?:\/\/)/i.test(testUrl)) {
      testUrl = 'https://' + testUrl;
  }
  try {
      new URL(testUrl);
      return true;
  } catch (e) {
      return false;
  }
};

const validateEmail = (email: string): boolean => {
  if (!email) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const Report = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<ReportReact | null>(null);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);

  const handleRecaptchaChange = (value: string | null) => {
    setRecaptchaValue(value);
    if(value){
      trigger(); // Reruns validation when captcha is solved
    }
  };

  const formResolver = async (data: FormValues) => {
    const errors: FieldErrors<FormValues> = {};
    const submittedWebsites = data.website.filter(w => w.url && w.url.trim() !== '');

    if (submittedWebsites.length === 0) {
      if (!errors.website) errors.website = [];
      errors.website[0] = { url: { type: 'manual', message: "Website URL is required" } };
    } else {
      data.website.forEach((w, index) => {
        if (w.url && w.url.trim() !== '' && !validateWebsite(w.url)) {
          if (!errors.website) errors.website = [];
          errors.website[index] = { url: { type: 'manual', message: "Please enter a valid URL" } };
        }
      });
    }

    if (!data.email) {
      errors.email = { type: 'manual', message: "Email address is required" };
    } else if (!validateEmail(data.email)) {
      errors.email = { type: 'manual', message: "Please enter a valid email address" };
    }

    if (!data.privacyPolicy) {
      errors.privacyPolicy = { type: 'manual', message: "You must agree to the privacy policy" };
    }

    if (!recaptchaValue) {
        errors.name = { type: 'manual', message: "Please complete the reCAPTCHA verification." }; // Using 'name' field to show general error
    }

    return {
      values: data,
      errors: errors,
    };
  };

  const { register, handleSubmit, control, formState: { errors }, reset, trigger } = useForm<FormValues>({
    defaultValues: {
      website: [{ url: null }]
    },
    resolver: formResolver,
    mode: "onBlur"
  });

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: "website"
  });

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    try {
      setError(null);
      setLoading(true);
      setData(null);
      setSubmitSuccess(false);
      
      const { website, email, name, privacyPolicy } = data;
      
      const urls: string[] = website
        .filter((w) => w.url !== null && validateWebsite(w.url))
        .map((w) => {
          const url = (w.url as string).trim();
          if (!url.startsWith('http://') && !url.startsWith('https://')) {
            return 'https://' + url;
          }
          return url;
        });

      // Store original URLs for display
      const originalUrls: string[] = website
        .filter((w) => w.url !== null && validateWebsite(w.url))
        .map((w) => (w.url as string).trim());

      const privacyPolicyText = document.getElementById("privacyPolicyText")?.textContent || "";

      const report = await getReport({ 
        urls, 
        email, 
        name, 
        privacyPolicy, 
        recaptchaValue: recaptchaValue as string, 
        privacyPolicyText 
        });

      setData({
        url: report.url,
        results: report.results?.map((r, index) => ({
          success: r.success,
          message: r.message,
          data: r?.data,
          url: originalUrls[index] || r?.url, // Use original URL if available, fallback to backend URL
          issues: r?.data?.map((d) => d.nodes)?.reduce((a, b) => a + b, 0)
        }))
      });

      setSubmitSuccess(true);
      setTimeout(() => {
        document.getElementById("section-result")?.scrollIntoView({ behavior: "smooth" });
      }, 10);
      
      reset();
      
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaValue(null);
      }
      
    } catch (error: any) {
      console.error("error: ", error);
      if (error.response) {
        setError(error.response.data.message);
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddWebsite = () => {
    if (fields.length < 5) {
      append({ url: null });
    }
  };

  const handleRemoveWebsite = (index: number) => {
    if (fields.length > 1) {
      remove(index);
    }
  };
  
  // A generic error for reCAPTCHA that is not tied to a specific field.
  const generalError = errors.name?.message || error;

  return (
    <>
      <Helmet>
        <title>Report | Accessibility Audit Report</title>
        <link rel="canonical" href="https://accessibridge.com/report" />
        <meta name="description" content="Get your free accessibility audit report for your website" />
        <meta name="keywords" content="accessibility, audit, report, free, website" />
        <meta property="og:title" content="Report | Accessibility Audit Report" />
        <meta property="og:description" content="Get your free accessibility audit report for your website" />
        <meta property="og:url" content="https://accessibridge.com/report" />
        <meta property="og:type" content="website" />
      </Helmet>
      <Navbar />
      <main role="main">
        <section id="report" className="report" aria-labelledby="report-heading">
          <div className="report__container">
            <header className="report__header">
              <div className="report__header-inner">
                <h1 id="report-heading" className="report__title">
                    Get Your Accessibility Audit Report
                </h1>
                <p className="report__subtitle">
                  Enter your website URL and email to get your detailed accessibility report
                </p>
              </div>
            </header>

            <div className="report__form-container">
              <div className="report__form-wrapper">
                {/* Status messages area with aria-live region */}
                <div className="status-messages" aria-live="polite">
                  {generalError && (
                    <div className="report__error" role="alert">
                      <AlertCircle className="report__error-icon" aria-hidden="true" />
                      <p>{generalError}</p>
                    </div>
                  )}
                  
                  {submitSuccess && (
                    <div className="report__success" role="status">
                      <p>Your report has been generated successfully!</p>
                    </div>
                  )}
                </div>

                <form className="report__form" onSubmit={handleSubmit(onSubmit)} noValidate aria-labelledby="report-form-heading">
                  <div className="sr-only" id="report-form-heading">Accessibility Report Form</div>
                  
                  <p className="report__form-instructions">
                    Required fields are marked with <span aria-hidden="true">*</span><span className="sr-only">asterisk</span>
                  </p>
                  
                  <div className="report__website-controls">
                    <button
                      type="button"
                      onClick={handleAddWebsite}
                      disabled={fields.length >= 5 || loading}
                      className="report__add-button"
                      aria-label="Add another website URL"
                  >
                      <Plus size={16} aria-hidden="true" />
                    </button>
                    <span>Add website URL (maximum 5)</span>
                  </div>

                {fields.map((field, index) => (
                    <div 
                      key={field.id} 
                      className={`report__form-group ${
                        focusedField === `website-${index}` ? "report__form-group--focused" : ""
                      } ${errors.website?.[index] ? "report__form-group--error" : ""}`}
                    >
                      <label 
                        htmlFor={`website-${index}`} 
                        className="report__label"
                        id={`website-label-${index}`}
                      >
                        Website URL {index + 1} <span className="report__required" aria-hidden="true">*</span>
                        <span className="sr-only">(required)</span>
                      </label>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <div className="report__input-wrapper" style={{ flex: 1 }}>
                          <div className="report__input-icon">
                            <Globe className="report__icon" aria-hidden="true" />
                          </div>
                          <input
                            {...register(`website.${index}.url`)}
                            id={`website-${index}`}
                            name={`website.${index}.url`}
                            type="text"
                            inputMode="url"
                            className={`report__input ${errors.website?.[index] ? "report__input--error" : ""}`}
                            placeholder="www.example.com"
                            onFocus={() => setFocusedField(`website-${index}`)}
                            onBlur={() => setFocusedField(null)}
                            aria-required="true"
                            aria-invalid={!!errors.website?.[index]}
                            aria-describedby={errors.website?.[index] ? `website-error-${index}` : undefined}
                            aria-labelledby={`website-label-${index}`}
                            disabled={loading}
                          />
                        </div>
                        {fields.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveWebsite(index)}
                            className="report__remove-button"
                            aria-label={`Remove website URL ${index + 1}`}
                            disabled={loading}
                          >
                            <X size={16} aria-hidden="true" />
                          </button>
                        )}
                      </div>
                      {errors.website?.[index]?.url && (
                        <div id={`website-error-${index}`} className="report__error-message" aria-live="polite">
                          <AlertCircle className="report__error-icon" size={16} aria-hidden="true" />
                          {errors.website[index]?.url?.message}
                        </div>
                      )}
                    </div>
                  ))}

                  <div className="report__form-grid">
                    <div className={`report__form-group ${focusedField === "email" ? "report__form-group--focused" : ""} ${errors.email ? "report__form-group--error" : ""}`}>
                      <label htmlFor="email" className="report__label" id="email-label">
                        Email <span className="report__required" aria-hidden="true">*</span>
                        <span className="sr-only">(required)</span>
                      </label>
                      <div className="report__input-wrapper">
                        <div className="report__input-icon">
                          <Mail className="report__icon" aria-hidden="true" />
                        </div>
                        <input
                          {...register("email")}
                          id="email"
                          name="email"
                          type="email"
                          className={`report__input ${errors.email ? "report__input--error" : ""}`}
                          placeholder="your@email.com"
                          onFocus={() => setFocusedField("email")}
                          onBlur={() => setFocusedField(null)}
                          aria-required="true"
                          aria-invalid={!!errors.email}
                          aria-describedby={errors.email ? "email-error" : undefined}
                          aria-labelledby="email-label"
                          disabled={loading}
                        />
                      </div>
                      {errors.email && (
                        <div id="email-error" className="report__error-message" aria-live="polite">
                          <AlertCircle className="report__error-icon" size={16} aria-hidden="true" />
                          {errors.email.message}
                        </div>
                      )}
                    </div>

                    <div className={`report__form-group ${focusedField === "name" ? "report__form-group--focused" : ""}`}>
                      <label htmlFor="name" className="report__label" id="name-label">
                        Your Name
                      </label>
                      <div className="report__input-wrapper">
                        <div className="report__input-icon">
                          <User className="report__icon" aria-hidden="true" />
                        </div>
                        <input
                          {...register("name")}
                          id="name"
                          name="name"
                          type="text"
                          className="report__input"
                          placeholder="Your Name"
                          onFocus={() => setFocusedField("name")}
                          onBlur={() => setFocusedField(null)}
                          aria-labelledby="name-label"
                          disabled={loading}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="report__consent">
                    <div className={`report__form-group ${errors.privacyPolicy ? "report__form-group--error" : ""}`}>
                      <div className="report__checkbox-wrapper" id="privacyPolicyText">
                        <input
                          {...register("privacyPolicy")}
                          id="privacy-policy"
                          name="privacyPolicy"
                          type="checkbox"
                          className={`report__checkbox ${errors.privacyPolicy ? "report__checkbox--error" : ""}`}
                          aria-required="true"
                          aria-invalid={!!errors.privacyPolicy}
                          aria-describedby={errors.privacyPolicy ? "privacy-error" : undefined}
                          disabled={loading}
                        />
                        <label htmlFor="privacy-policy" className="report__checkbox-label">
                          I agree to the processing of personal data for the purpose of receiving a detailed WCAG report
                          for my website and information about the company's products and services. More information
                          about data protection{" "}
                          <a 
                            href={PrivatePolicy} 
                            className="report__link"
                            target="_blank" 
                            rel="noopener noreferrer"
                            aria-label="Read our Privacy Policy (opens in a new tab)"
                          >
                            in our Privacy Policy
                          </a>
                          .
                        </label>
                      </div>
                      {errors.privacyPolicy && (
                        <div id="privacy-error" className="report__error-message" aria-live="polite">
                          <AlertCircle className="report__error-icon" size={16} aria-hidden="true" />
                          {errors.privacyPolicy.message}
                        </div>
                      )}
                    </div>

                    <p className="report__consent-paragraph">
                    By clicking "GET YOUR FREE REPORT NOW", you agree to our Privacy Policy. By
                    providing your email and name, you will receive a detailed WCAG report for your website. Your
                    personal data will be stored for a maximum of 6 months. You can withdraw your consent at any time by
                      contacting us at{" "}
                      <a 
                        href="mailto:privacy@mnb.solutions" 
                        className="report__link"
                      >
                      privacy@mnb.solutions
                    </a>
                    .
                    </p>
                  </div>

                  <div className="report__recaptcha-container">
                    <div 
                      className="report__recaptcha-wrapper" 
                      tabIndex={-1}
                    >
                  <ReCAPTCHA
                    ref={recaptchaRef}
                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
                    onChange={handleRecaptchaChange}
                        className="report__recaptcha"
                        aria-label="reCAPTCHA verification"
                  />
                    </div>
                  </div>

                                    <div className="report__submit-container">                    <button                      type="submit"                      disabled={!recaptchaValue || loading}                      className={`report__submit-button ${                        recaptchaValue && !loading ? "report__submit-button--active" : "report__submit-button--disabled"                      }`}                      aria-disabled={!recaptchaValue || loading}                      aria-busy={loading}                    >                      {loading ? (                        <>                          <Loader2 className="report__submit-spinner" aria-hidden="true" />                          <span>PROCESSING...</span>                        </>                      ) : (                        <>                          <span>GET YOUR FREE REPORT NOW</span>                          <ArrowRight className="report__submit-icon" aria-hidden="true" />                        </>                      )}                    </button>                  </div>
            </form>
              </div>
            </div>
          </div>
        </section>

          {data && (
          <section id="section-result" className="report">
            <div className="report__container">
              <div className="report__results-section" aria-live="polite" aria-atomic="true">
                <h2 className="report__results-title">AccessiBridge Report Overview</h2>
                  {data.results.map((result, index) => (
                    <ReportTable key={index} report={result} fullReportUrl={data.url} />
                  ))}
              </div>
            </div>
          </section>
        )}
        
        {loading && <LoadingLoader />}
      </main>
      <Footer />
    </>
  );
};

export default Report;
